import { Injectable, inject, signal } from '@angular/core';
import { CompetitionService } from '../http/competition.service';
import { newsApi } from '../http/news.api';
import { NewsItem } from '../models/types';

interface ReadMarker { time: number; ids: string[]; }

@Injectable({ providedIn: 'root' })
export class NewsReadState {
  private readonly competition = inject(CompetitionService);
  private readonly memory = new Map<string, ReadMarker>();
  private scope = '';
  private activeKey = '';
  private generation = 0;
  private latest: NewsItem[] = [];
  readonly hasUnread = signal(false);

  setScope(userId: string, leagueId: string, clubId: string): void {
    const scope = JSON.stringify([userId, leagueId, clubId]);
    if (scope === this.scope) return;
    this.scope = scope;
    this.generation++;
    this.activeKey = '';
    this.latest = [];
    this.hasUnread.set(false);
  }

  async refresh(userId: string): Promise<void> {
    const generation = this.generation;
    try {
      const context = await this.competition.resolveCurrentCompetitionContext();
      if (generation !== this.generation || context.status !== 'JOINED' || !context.clubId) return;
      const unitId = context.clubId;
      const response = await newsApi.getNewsByUnit(unitId);
      if (generation !== this.generation) return;
      this.activeKey = this.key(userId, unitId);
      this.latest = response.items || [];
      this.recompute();
    } catch {
      // A failed refresh must not acknowledge unread news or disrupt navigation.
    }
  }

  markRead(userId: string, unitId: string, items: NewsItem[]): void {
    if (!userId || !items.length) return;
    const key = this.key(userId, unitId);
    const previous = this.read(key);
    const time = Math.max(previous.time, ...items.map(item => this.time(item)));
    const ids = new Set(previous.time === time ? previous.ids : []);
    items.filter(item => this.time(item) === time).forEach(item => ids.add(item.id));
    const marker = { time, ids: [...ids] };
    this.memory.set(key, marker);
    try { localStorage.setItem(key, JSON.stringify(marker)); } catch { /* Use memory when storage is unavailable. */ }
    if (key === this.activeKey) this.recompute();
  }

  syncStorage(): void {
    this.memory.clear();
    this.recompute();
  }

  private key(userId: string, unitId: string): string {
    return 'lom.news.read.v1:' + JSON.stringify([userId, unitId]);
  }

  private time(item: NewsItem): number {
    return Date.parse(item.publishedAt || item.occurredAt) || 0;
  }

  private read(key: string): ReadMarker {
    const cached = this.memory.get(key);
    if (cached) return cached;
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      if (value && Number.isFinite(value.time) && Array.isArray(value.ids) && value.ids.every((id: unknown) => typeof id === 'string')) return value;
    } catch { /* Missing or invalid browser storage is treated as unread. */ }
    return { time: -1, ids: [] };
  }

  private recompute(): void {
    const marker = this.read(this.activeKey);
    this.hasUnread.set(this.latest.some(item => {
      const time = this.time(item);
      return time > marker.time || (time === marker.time && !marker.ids.includes(item.id));
    }));
  }
}
